import {
  BadgeCheck,
  ChevronDown,
  CircleAlert,
  GraduationCap,
  MapPinned,
  WalletCards
} from "lucide-react";
import type { CandidateProfile } from "../types";

type Props = {
  profile: CandidateProfile;
  onChange: (profile: CandidateProfile) => void;
  expanded: boolean;
  onToggle: () => void;
};

const provinces = [
  "北京",
  "天津",
  "河北",
  "山西",
  "内蒙古",
  "辽宁",
  "吉林",
  "黑龙江",
  "上海",
  "江苏",
  "浙江",
  "安徽",
  "福建",
  "江西",
  "山东",
  "河南",
  "湖北",
  "湖南",
  "广东",
  "广西",
  "海南",
  "重庆",
  "四川",
  "贵州",
  "云南",
  "西藏",
  "陕西",
  "甘肃",
  "青海",
  "宁夏",
  "新疆"
];

export function profileCompletion(profile: CandidateProfile) {
  const checks = [
    profile.province,
    profile.examYear,
    profile.subjectType,
    profile.score || profile.rank,
    profile.familyBudget,
    profile.targetCities
  ];
  return Math.round((checks.filter(Boolean).length / checks.length) * 100);
}

export function ProfilePanel({ profile, onChange, expanded, onToggle }: Props) {
  const completion = profileCompletion(profile);
  const update = (key: keyof CandidateProfile, value: string) => {
    onChange({ ...profile, [key]: value });
  };

  return (
    <section className="profile-card" aria-labelledby="profile-title">
      <button className="profile-card__header" type="button" onClick={onToggle}>
        <span className="profile-card__icon" aria-hidden="true">
          <GraduationCap size={21} />
        </span>
        <span>
          <span className="eyebrow">考生档案</span>
          <strong id="profile-title">{completion === 100 ? "信息够用了" : "先把底牌亮出来"}</strong>
        </span>
        <span className="profile-card__progress">{completion}%</span>
        <ChevronDown
          className={expanded ? "chevron chevron--open" : "chevron"}
          size={18}
          aria-hidden="true"
        />
      </button>

      <div className="progress-track" aria-label={`档案完整度 ${completion}%`}>
        <span style={{ width: `${completion}%` }} />
      </div>

      {expanded && (
        <div className="profile-form">
          <div className="form-row form-row--two">
            <label>
              <span>所在省份 *</span>
              <select
                value={profile.province}
                onChange={(event) => update("province", event.target.value)}
              >
                <option value="">请选择</option>
                {provinces.map((province) => (
                  <option key={province} value={province}>
                    {province}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>高考年份 *</span>
              <input
                inputMode="numeric"
                value={profile.examYear}
                onChange={(event) => update("examYear", event.target.value)}
                placeholder="2026"
              />
            </label>
          </div>

          <label>
            <span>选科 / 科类 *</span>
            <input
              value={profile.subjectType}
              onChange={(event) => update("subjectType", event.target.value)}
              placeholder="例如：物理 + 化学 + 生物"
            />
          </label>

          <div className="form-row form-row--two">
            <label>
              <span>预估 / 实考分数</span>
              <input
                inputMode="numeric"
                value={profile.score}
                onChange={(event) => update("score", event.target.value)}
                placeholder="例如：612"
              />
            </label>
            <label>
              <span>全省位次</span>
              <input
                inputMode="numeric"
                value={profile.rank}
                onChange={(event) => update("rank", event.target.value)}
                placeholder="位次比分数更重要"
              />
            </label>
          </div>

          <label>
            <span>家庭可承受年预算 *</span>
            <div className="input-with-icon">
              <WalletCards size={16} aria-hidden="true" />
              <input
                value={profile.familyBudget}
                onChange={(event) => update("familyBudget", event.target.value)}
                placeholder="例如：3 万以内 / 可接受中外合作"
              />
            </div>
          </label>

          <label>
            <span>目标城市 / 区域 *</span>
            <div className="input-with-icon">
              <MapPinned size={16} aria-hidden="true" />
              <input
                value={profile.targetCities}
                onChange={(event) => update("targetCities", event.target.value)}
                placeholder="例如：长三角优先，不去极寒地区"
              />
            </div>
          </label>

          <label>
            <span>兴趣与排斥项</span>
            <textarea
              rows={2}
              value={profile.interests}
              onChange={(event) => update("interests", event.target.value)}
              placeholder="喜欢什么、坚决不学什么，都直说"
            />
          </label>

          <label>
            <span>家庭行业资源</span>
            <textarea
              rows={2}
              value={profile.familyResources}
              onChange={(event) => update("familyResources", event.target.value)}
              placeholder="例如：父母在电力系统；没有可用行业资源"
            />
          </label>

          <label>
            <span>身体 / 升学限制</span>
            <textarea
              rows={2}
              value={profile.constraints}
              onChange={(event) => update("constraints", event.target.value)}
              placeholder="色弱、近视、是否接受读研、是否考公等"
            />
          </label>

          <div className={completion >= 80 ? "profile-tip profile-tip--good" : "profile-tip"}>
            {completion >= 80 ? <BadgeCheck size={17} /> : <CircleAlert size={17} />}
            <span>
              {completion >= 80
                ? "核心信息齐了，可以开始做取舍。"
                : "至少补齐省份、选科、分数或位次、预算和城市。"}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
